import type { CSSProperties } from "react";
import type { WebOrdersReport } from "../../../api/types";
import type { ShopColorSlot } from "../../../utils/shopColorSlots";
import { percentOf } from "./webOrdersText";

interface Part {
  key: string;
  name: string;
  count: number;
  slot: ShopColorSlot;
}

/**
 * G-143 (OD-218) — how the period's web orders were shared between the
 * shops: one strip, one legend. Owners only, with every shop selected. The
 * colour follows the shop (`shopColorSlots`), never its rank.
 */
export function ShopDistribution({
  report,
  slots,
}: {
  report: WebOrdersReport;
  slots: Map<string, ShopColorSlot>;
}) {
  const total = report.totals.received;
  const parts: Part[] = report.shops.map((s) => ({
    key: s.shop.id,
    name: s.shop.name,
    count: s.received,
    slot: slots.get(s.shop.id) ?? "other",
  }));
  if (report.notTaken.received > 0) {
    parts.push({ key: "not-taken", name: "Not taken", count: report.notTaken.received, slot: "other" });
  }
  if (total === 0) return null;
  const summary = parts.map((p) => `${p.name} ${p.count}`).join(", ");

  return (
    <div className="wo-dist">
      <div className="wo-dist-strip" role="img" aria-label={`${summary} of ${total} web orders`}>
        {parts.map((p) => (
          <span
            key={p.key}
            data-slot={p.slot}
            title={`${p.name}: ${p.count} of ${total}`}
            style={{ "--share": `${percentOf(p.count, total).toFixed(1)}%` } as CSSProperties}
          />
        ))}
      </div>
      <ul className="wo-dist-legend">
        {parts.map((p) => (
          <li key={p.key}>
            <span className="wo-swatch" data-slot={p.slot} aria-hidden="true" />
            <strong>{p.name}</strong>
            <span className="muted">
              {p.count} · {Math.round(percentOf(p.count, total))}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
